/* tslint:disable:no-trailing-whitespace */
/* tslint:disable: object-literal-sort-keys */

import omit from 'lodash/omit';
import moment = require('moment');
import version from '../json/version';
import { Condition, IGroup, } from './Group';
import { IGroupButton } from './GroupButton';
import { IGroupLine } from './GroupLine';
import { IQuery } from './Query';
import { AttributeType, IAttribute, IRule, ValueType } from './Rule';

/**
 * Helper object containing the logic needed to manipulate the groups the where rules
 * for the query builder.
 */
const helper = {
    /**
     * Return a fairly-random new id to be used for groups and rules.
     */
    generateId: (): string => `id-${Math.random().toString(36).substring(2, 15)}`,
    /**
     * Create an empty rule in the given default group.
     */
    emptyRule: (defaultGroupId: string): IRule => helper.prepRule({ $$isNew: true } as IRule, defaultGroupId, null),
    /**
     * Prepare multiple rules for the given default group id.  Also, remove empty rules.
     * See prepRule() for more information.
     */
    prepRules: (rules: IRule[], defaultGroupId: string, groups: IGroup[]) => {
        return rules.map((r) => helper.prepRule(r, defaultGroupId, groups)).filter((r) => !helper.isEmpty(r));
    },
    /**
     * Remove utlitity attributes that are used for rendering but not to be exposed to
     * consumers.  These include '$$ruleid', '$$groupButtons', '$$isNew'.
     */
    unprepRules: (rules: IRule[]): IRule[] => rules.map((it: any) => omit(it, '$$ruleid', '$$groupButtons', '$$isNew')),
    /**
     * Remove utlitity attributes that are used for rendering but not to be exposed to
     * consumers.  These include '$$groupLines'.
     */
    unprepGroups: (groups: IGroup[]): IGroup[] => groups.map((it: any) => omit(it, '$$groupLines') as IGroup),
    /**
     * Return the group id that the rule is directly in (not under);
     */
    getGroupId: (rule: IRule): string => rule.groupIds[rule.groupIds.length - 1],
    /**
     * Return true if the rule is directly in the given group (not under the group).
     */
    isRuleInGroup: (rule: IRule, groupId: string): boolean => helper.getGroupId(rule) === groupId,
    /**
     * Return true if the rule directly in or under given group.
     */
    isRuleInOrUnderGroup: (rule: IRule, groupId: string): boolean => rule.groupIds.indexOf(groupId) >= 0,
    /**
     * Return true if the rules are in the same direct group.
     */
    inSameGroup: (rule1: IRule, rule2: IRule): boolean => helper.getGroupId(rule1) === helper.getGroupId(rule2),
    /**
     * Return true if the two rules are the same based on $$ruleid; false otherwise.
     */
    isSameRule: (rule1: IRule, rule2: IRule): boolean => rule1.$$ruleid === rule2.$$ruleid,
    /**
     * Return true if there is at least one of the given rules that is not already in the
     * given group and so can be moved to the given group.
     */
    canMoveRulesToGroup(rules: IRule[], groupId: string): boolean {
        if (!rules || !rules.length) {
            return false;
        }

        // ignore the rules that are already part of the group
        rules = rules.filter((r) => !helper.isRuleInGroup(r, groupId));

        if (!rules.length) {
            return false;
        }

        return true;
    },
    /**
     * Inspect all rules to find the distinct set of groupIds that exist under the given
     * group id.
     */
    getNestedGroupIds: (groupId: string, where: IRule[]): string[] => {
        const nestedGroups = new Set<string>();

        where.forEach((r) => {
            const groupIndex = r.groupIds.indexOf(groupId);
            if (groupIndex >= 0) {
                for (let i = groupIndex; i < r.groupIds.length; i++) {
                    nestedGroups.add(r.groupIds[i]);
                }
            }
        });

        return Array.from(nestedGroups);
    },
    /**
     * Inspect all rules to find the distinct set of child groupIds that exist directly
     * under the given group id.
     */
    getChildGroupIds: (groupId: string, where: IRule[]): string[] => {
        const childGroupIds = new Set<string>();

        where.forEach((r) => {
            const groupIndex = r.groupIds.indexOf(groupId);
            if (groupIndex >= 0) {
                const childGroupId = r.groupIds[groupIndex + 1];
                if (childGroupId != null) {
                    childGroupIds.add(childGroupId);
                }
            }
        });

        return Array.from(childGroupIds);
    },
    /**
     * Inspect all rules to find the distinct set of child rule ids that exist directly
     * under the given group id.
     */
    getChildRuleIds: (groupId: string, where: IRule[]): string[] => {
        const childRuleIds = new Set<string>();

        where.forEach((r) => {
            const groupIndex = r.groupIds.indexOf(groupId);
            if (groupIndex >= 0 && groupIndex === r.groupIds.length - 1) {
                childRuleIds.add(r.$$ruleid);
            }
        });

        return Array.from(childRuleIds);
    },
    /**
     * Return the group button rule index and centered indicator where the group button will appear.
     */
    getGroupButtonInfo: (groupId: string, where: IRule[]): { index: number, centered: boolean } => {
        let i = 0;
        while (i < where.length) {
            const rule = where[i];
            const groupButton = rule.$$groupButtons.find((rl) => rl.groupId === groupId && rl.showGroupButton);
            if (rule.$$groupButtons.find((rl) => rl.groupId === groupId && rl.showGroupButton)) {
                return { index: i, centered: groupButton.centerGroupButton };
            }
            i++;
        }
        return null;
    },
    /**
     * Deletes the rule from the group and removes the group itself if no more rules exist under it.
     */
    deleteRule: (rule: IRule, query): IQuery => {
        query.where = query.where.filter((r) => !helper.isSameRule(rule, r));
        query = helper.removeEmptyGroups(query);
        return query;
    },
    /**
     * Create a new group under the given group using the given rules.  This essentially moves the
     * rules under the parent group and then creates the child group under the parent using those rules.
     */
    createGroupFromRules: (rules: IRule[], groupId: string, query: IQuery, supportedGroupDepth: number): IQuery => {
        query = helper.moveRulesToGroup(rules, groupId, query);
        query = helper.createGroup(rules, query, supportedGroupDepth);
        return query;
    },
    /**
     * Calls canMoveRulesToGroup() to find the rules that are not already in the given group
     * and moves the rules into the given group.  Returns a new query.  This operation could
     * result in the removal of groups (via call to removeEmptyGroups()) if there are no longer
     * any rules in a given group because the rules have been moved out. Additionally, the logic
     * ensures that rules having the same direct group are grouped together (via call to
     * organizeRules).
     */
    moveRulesToGroup: (rules: IRule[], groupId: string, query: IQuery): IQuery => {
        if (!helper.canMoveRulesToGroup(rules, groupId)) {
            return query;
        }

        const { groups } = query;
        let { where } = query;

        const originalWhere = [...where];

        // ignore the rules that are already part of the group
        rules = rules.filter((r) => !helper.isRuleInGroup(r, groupId));

        // remove rules to be moved from where
        where = [...where].filter((r) => rules.findIndex((innerR) => helper.isSameRule(innerR, r)) < 0);

        let rulesInGroup = where.filter((r) => helper.isRuleInGroup(r, groupId));

        // if there are no existing rules living directly in target group, find last rule that references group at all
        if (!rulesInGroup.length) {
            rulesInGroup = where.filter((r) => helper.isRuleInOrUnderGroup(r, groupId));
        }

        let lastRuleInGroup = rulesInGroup[rulesInGroup.length - 1];
        let lastRuleInGroupIndex = where.indexOf(lastRuleInGroup);

        // if we found no rules referencing the new group at all, then the rules that existed under the group
        // must all be moving. find one of the rules that references the target group.
        if (!lastRuleInGroup) {
            lastRuleInGroup = rules.find((r) => helper.isRuleInOrUnderGroup(r, groupId));
            lastRuleInGroupIndex = originalWhere.findIndex((r) => helper.isSameRule(r, lastRuleInGroup));
        }

        // set new group on rules
        const indexOfGroupId = lastRuleInGroup.groupIds.indexOf(groupId);
        rules.forEach((r) => r.groupIds = lastRuleInGroup.groupIds.slice(0, indexOfGroupId + 1));

        // insert rules into new indices
        where.splice(lastRuleInGroupIndex + 1, 0, ...rules);

        query = { groups, where };

        query = helper.removeEmptyGroups(query);
        query = helper.organizeRules(query);

        return query;
    },
    /**
     * Remove the empty groups that are now obsolete because they no longer contain
     * any rules.
     */
    removeEmptyGroups: (query: IQuery): IQuery => {
        const representedGroupIds = new Set<string>();

        const { where } = query;
        let { groups } = query;

        const defaultGroupId = groups[0].id;

        where.forEach((r) => r.groupIds.forEach((id) => representedGroupIds.add(id)));

        groups = groups.filter((g) => representedGroupIds.has(g.id) || g.id === defaultGroupId);

        return { groups, where };
    },
    /**
     * Moving rules can sometimes lead to situations where two rules in the
     * same group are not next to each other.  In that case, we have to organize
     * these rules so they appear together or else rule lines will not render
     * properly. This function organizes rules in the group to be together.
     */
    organizeRules: (query: IQuery): IQuery => {
        // look for rules in same group that are not contiguous
        const { groups } = query;
        let { where } = query;

        const indicesByGroup = new Map<string, number[]>();
        const originalWhere = [...where];

        // create map of group and indexes
        where.forEach((r, index) => {
            const groupId = helper.getGroupId(r);
            const indices = indicesByGroup.get(groupId) || [];
            indices.push(index);
            indicesByGroup.set(groupId, indices);
        });

        indicesByGroup.forEach((indices) => {
            let prevIndex = null;
            let moveRemaining = false;
            let startIndexToMove = -1;
            indices.forEach((index, i) => {
                if (moveRemaining ) {
                    return;
                }

                // non-contiguous so move remaining rules
                if (prevIndex != null && prevIndex !== index - 1) {
                    moveRemaining = true;
                    startIndexToMove = i;
                    return;
                }
                prevIndex = index;
            });

            if (moveRemaining) {
                const lastRule = originalWhere[prevIndex];
                const lastRuleInGroupIndex = where.indexOf(lastRule);

                const rulesToMove = indices.slice(startIndexToMove).map((i) => originalWhere[i]);

                // remove rules to be moved from where
                where = where.filter((r) => rulesToMove.findIndex((innerR) => helper.isSameRule(innerR, r)) < 0);

                // insert rules into new indices
                where.splice(lastRuleInGroupIndex + 1, 0, ...rulesToMove);
            }
        });

        return { groups, where };
    },
    /**
     * Adds a new rule to the given group.
     */
    addRule: (groupId: string, query: IQuery): { query: IQuery, rule: IRule } => {
        // intentionally fake default group id so moveRulesToGroup won't ignore
        const fakeGroupId = helper.generateId();
        const rule = helper.emptyRule(fakeGroupId);

        query = helper.moveRulesToGroup([rule], groupId, query);

        return { query, rule };
    },
    /**
     * Create a duplicate of the given rule.
     */
    addDuplicateRule: (rule: IRule, query: IQuery): { query: IQuery, rule: IRule } => {
        // intentionally fake default group id so moveRulesToGroup won't ignore
        const duplicate = helper.emptyRule(helper.generateId());

        duplicate.attribute = rule.attribute;
        duplicate.operator = rule.operator;
        duplicate.value = rule.value;
        duplicate.valueB = rule.valueB;

        const groupId = helper.getGroupId(rule);

        query = helper.moveRulesToGroup([duplicate], groupId, query);

        return { query, rule: duplicate };
    },
    /**
     * Return true if the given rules can be used to create a new group, false otherwise.  The
     * supported group depth is used to prevent a group from being created deep than the support
     * group depth.
     */
    canCreateGroup: (rules: IRule[], supportedGroupDepth: number): boolean => {
        if (!rules || rules.length < 2) {
            return false;
        }

        const firstRule = rules[0];

        // only look at rules having same groups as first rule
        const sameGroupingLevelRules = rules.filter((r) => helper.inSameGroup(firstRule, r));

        // rules must be at same grouping level
        if (rules.length !== sameGroupingLevelRules.length) {
            return false;
        }

        // rules must not already have max supported depth
        if (firstRule.groupIds.length >= supportedGroupDepth) {
            return false;
        }

        return true;
    },
    /**
     * Create a group for the given rules.  The canCreateGroup is called to ensure that the
     * given rules are valid to create a group.
     */
    createGroup: (rules: IRule[], query: IQuery, supportedGroupDepth: number): IQuery => {
        if (!helper.canCreateGroup(rules, supportedGroupDepth)) {
            return query; // do nothing
        }

        const { groups, where } = query;

        const firstRule = rules[0];

        // create group
        const group: IGroup = {
            condition: Condition.And,
            id: helper.generateId(),
        };

        groups.push(group);

        firstRule.groupIds.push(group.id);

        query = { groups, where };

        return helper.moveRulesToGroup(rules.slice(1, rules.length), group.id, query);
    },
    /**
     * Remove the given group and move the composed rules to the next higher group.
     */
    ungroup: (groupId: string, query: IQuery): IQuery => {
        const { where } = query;
        let { groups } = query;

        const isDefaultGroup = groups.findIndex((g) => g.id === groupId) === 0;

        let groupIdsToRemove = [groupId];
        if (isDefaultGroup) {
            groupIdsToRemove = helper.getChildGroupIds(groupId, where);
        }

        // remove group unless first group
        groups = groups.filter((g) => groupIdsToRemove.indexOf(g.id) < 0);

        where.forEach((r) => {
            groupIdsToRemove.forEach((gid) => {
                const indexOfGroupId = r.groupIds.indexOf(gid);
                if (indexOfGroupId >= 0) {
                    r.groupIds = r.groupIds.slice(0, indexOfGroupId);
                }
            });
        });

        query = { groups, where };

        query = helper.removeEmptyGroups(query);

        return helper.organizeRules(query);
    },
    /**
     * Prepare the groups array for the query by ensuring that at least one
     * group exists.  This is the default group that rules at the top level will
     * appear withing.
     */
    prepGroups: (groups: IGroup[], where: IRule[]) => {
        groups = groups || [];

        groups = groups.map((g) => ({...g}));

        if (!groups.length) {
            // look for first groupid among rules and use that one.
            const firstRule = (where || []).find((r) => r.groupIds && r.groupIds.length > 0 && !!r.groupIds[0]);
            const defaultGroupId = firstRule ? firstRule.groupIds[0] : helper.generateId();

            groups.push({ // always have a default rule
                condition: Condition.And,
                id: defaultGroupId,
            });
        }

        return groups;
    },
    /**
     * Prepare the rules by populating the $$ruleid attribute if not already present.  The
     * $$ruleid attribute is used to identify the same rule and serve as a unique key. Additionally,
     * ensure the groupIds attribute is at minimum an array containing the default group id.
     * Lastly, add an empty $$groupButtons array to be used for rendering of the rule lines and
     * groups section of the query builder.
     */
    prepRule: (rule: IRule, defaultGroupId: string, groups: IGroup[]): IRule => {
        rule = rule.$$ruleid ? rule : Object.assign({}, rule, { $$ruleid: helper.generateId() });

        rule.groupIds = rule.groupIds || [];

        if (!rule.groupIds.length) {
            rule.groupIds.push(defaultGroupId);
        } else { // ensure all group ids are present in groups collection, otherwise use default group
            // look for a groupId that is not in the groups collection
            const anyMissingGroupIds = rule.groupIds.find((id) => groups.findIndex((g) => g.id === id) < 0);
            if (anyMissingGroupIds) { // use default group because we don't want rule to disappear
                rule.groupIds = [defaultGroupId];
            }
        }

        if (!rule.$$groupButtons) {
            rule.$$groupButtons = [];
        }

        return rule;
    },
    /**
     * Prepare the $$groupButtons attribute for each rule.  $$groupButtons is an array attribute containing information
     * about the group information to be rendered for each rule.  The $$groupButtons attribute should be
     * updated (and thus, this method should be called) whenever the query's where changes.
     */
    prepGroupButtons: (query: IQuery, supportedGroupDepth: number): void =>  {
        const { groups, where } = query;

        const groupsById = new Map<string, IGroup>();
        groups.forEach((g) => groupsById.set(g.id, g));

        const displayedGroupButtonIds = new Set<string>();
        const onlyOneRule = where.length === 1;

        const groupIdStartIndices = new Map<string, number>();
        const groupButtonIndices = new Map<string, { index: number, center: boolean}>();

        // on the first pass arrange the group buttons
        where.forEach((rule, ruleIndex) => {
            // if only one rule, show no groups or lines
            if (onlyOneRule) {
                rule.$$groupButtons = [];
                return;
            }

            const groupButtons: IGroupButton[] = [];

            rule.groupIds.forEach((groupId, index) => {
                if (!groupIdStartIndices.has(groupId)) {
                    groupIdStartIndices.set(groupId, index);
                }

                const group = groupsById.get(groupId);
                const first = ruleIndex === 0 || groupId !== where[ruleIndex - 1].groupIds[index];
                const last = ruleIndex === where.length - 1 || groupId !== where[ruleIndex + 1].groupIds[index];

                if (first) {
                    groupIdStartIndices.set(groupId, ruleIndex);
                }

                if (last) {
                    const firstIndex = groupIdStartIndices.get(groupId);
                    const lastIndex = ruleIndex;
                    const middleIndex = firstIndex + ((lastIndex - firstIndex) / 2);
                    const middleIndexFloor = Math.floor(middleIndex);
                    const center = middleIndexFloor === middleIndex;

                    groupButtonIndices.set(groupId, { index: middleIndexFloor, center });
                }

                groupButtons.push({
                    condition: group.condition,
                    groupId: group.id,
                });
            });

            rule.$$groupButtons = groupButtons;
        });

        // next pass builds displayed group buttons
        where.forEach((rule, ruleIndex) => {
            const groupButtons = rule.$$groupButtons;
            let i = 0;
            while (i < groupButtons.length) {
                const groupButton = groupButtons[i];

                const groupButtonLocation = groupButtonIndices.get(groupButton.groupId);
                if (ruleIndex < groupButtonLocation.index) {
                    i++;
                    continue;
                }

                if (!displayedGroupButtonIds.has(groupButton.groupId)) {
                    displayedGroupButtonIds.add(groupButton.groupId);

                    groupButton.showGroupButton = true;
                    groupButton.disableAddSelectedRules = true;

                    if (groupButtonLocation.center) {
                        groupButton.centerGroupButton = true;
                    }

                    if (i === 0) { // top level group
                        if (helper.getChildGroupIds(groupButton.groupId, where).length === 0) { // if no group children
                            groupButton.disableUngroup = true;
                        }
                    }

                    // don't allow add group when already at max supported group level
                    if ((i + 1) >= supportedGroupDepth) {
                        groupButton.disableAddGroup = true;
                    }
                }

                i++;
            }
        });
    },
    /**
     * Prepare the $$groupLines attribute for each group.  $$groupLines is an array attribute containing information
     * about the group lines to be drawn between groups or from groups to rules.  The $$groupLines attribute should be
     * updated (and thus, this method should be called) whenever the query's where changes.
     */
    prepGroupLines: (query: IQuery): void =>  {
        const { groups, where } = query;

        groups.forEach((group) => {
            const lines: IGroupLine[] = [];

            const groupButtonInfo = helper.getGroupButtonInfo(group.id, where);
            if (groupButtonInfo) {
                // get child groups for this group and draw lines
                helper.getChildGroupIds(group.id, where).forEach((id) => {
                    const childGroupButtonInfo = helper.getGroupButtonInfo(id, where);

                    let orientation = childGroupButtonInfo.index <= groupButtonInfo.index ? 'top' : 'bottom';

                    // if indices are the same...
                    // if the child is centered and the group button is not, then top
                    // if the child is not centered and the group button is, then bottom
                    if (childGroupButtonInfo.index === groupButtonInfo.index) {
                        if (!childGroupButtonInfo.centered && groupButtonInfo.centered) {
                            orientation = 'bottom';
                        }
                    }

                    const keys = [
                        group.id,
                        id,
                        groupButtonInfo.index,
                        childGroupButtonInfo.index,
                        childGroupButtonInfo.centered,
                        groupButtonInfo.centered
                    ];

                    lines.push({
                        end: id,
                        key: keys.join('_'),
                        orientation,
                        start: group.id,
                    });
                });

                // get direct child rules for this group and draw lines
                helper.getChildRuleIds(group.id, where).forEach((id) => {
                    const childRuleIndex = where.findIndex((r) => r.$$ruleid === id);
                    const orientation = childRuleIndex <= groupButtonInfo.index ? 'top' : 'bottom';

                    const keys = [
                        group.id,
                        id,
                        groupButtonInfo.index,
                        childRuleIndex,
                        groupButtonInfo.centered
                    ];

                    lines.push({
                        end: id,
                        key: keys.join('_'),
                        orientation,
                        start: group.id,
                    });
                });
            }

            group.$$groupLines = lines;
        });
    },
    /**
     * Return the operator label for the attribute and operator combination.
     */
    getOperatorLabel: (attribute: IAttribute, operator: string): string => {
        let operatorLabel = '';
        if (attribute && attribute.operators && operator) {
            const matchedOperator = attribute.operators.find((o) => o.value === operator);
            if (matchedOperator) {
                operatorLabel = matchedOperator.label;
            }
        }
        return operatorLabel;
    },
    /**
     * Return the cleansed value, creating default for missing value.
     */
    getCleansedValue: (attribute: IAttribute, value: ValueType): ValueType => {
        let cleanValue;

        if (attribute) {
            switch (attribute.type) {
                case AttributeType.Int:
                case AttributeType.String:
                case AttributeType.Enum:
                    cleanValue = value || '';
                    break;
                case AttributeType.MultiSelectEnum:
                    cleanValue = Array.isArray(value) ? value : [];
                    break;
                case AttributeType.RadioChoiceEnum:
                    cleanValue = value || '';
                    break;
                case AttributeType.Date:
                case AttributeType.Lookup:
                    cleanValue = value || null;
                    break;
            }
        }

        return cleanValue;
    },
    /**
     * Return the value label for a rule based on attribute information.
     */
    getValueLabel: (attribute: IAttribute, value: ValueType, dateFormat?: string): string => {
        let dateValue;
        let enumValue;
        let textValue;

        if (attribute) {
            switch (attribute.type) {
                case AttributeType.Int:
                case AttributeType.String:
                    textValue = value || '';
                    break;
                case AttributeType.Lookup:
                    textValue = value ? (value as any).title || '' : '';
                    break;
                case AttributeType.Date:
                    dateValue = value || null;
                    break;
                case AttributeType.Enum:
                    enumValue = value || '';
                    break;
                case AttributeType.MultiSelectEnum:
                    enumValue = Array.isArray(value) ? value : [];
                    break;
                case AttributeType.RadioChoiceEnum:
                    enumValue = value || '';
                    break;
            }
        }

        let enumValueLabel = '';
        if (attribute && attribute.enum && enumValue) {
            if (attribute.type === AttributeType.Enum || attribute.type === AttributeType.RadioChoiceEnum) {
                const matchedEnum = attribute.enum.find((o) => o.value === enumValue);
                if (matchedEnum) {
                    enumValueLabel = matchedEnum.label;
                }
            } else if (attribute.type === AttributeType.MultiSelectEnum) { 
                enumValueLabel = attribute.enum.filter((o) => enumValue.includes(o.value)).map((o) => o.label).join(` ${Condition.Or} `);
            }
        }

        let dateValueLabel = '';
        if (dateValue) {
            dateValueLabel = moment(dateValue).format(dateFormat);
        }

        return textValue || enumValueLabel || dateValueLabel;
    },
    /**
     * Returns true if rule is fully populated, false otherwise.
     */
    isValid: (rule: IRule): boolean => {
        const { attribute, operator, value, valueB } = rule;

        const isValueBValid = !helper.requiresValueB(rule) || !!valueB;
        const isValueHidden = helper.hideValueInput(rule);
        const hasValue = (Array.isArray(value) ? value.length > 0 : !!value) || isValueHidden === true;

        return (attribute && operator && hasValue && isValueBValid) ? true : false;
    },
    /**
     * Return true if rule requires valueB according to selected operator.
     */
    requiresValueB: (rule: IRule): boolean => {
        const { attribute, operator } = rule;

        if (attribute && attribute.operators && operator) {
            const operatorOption = attribute.operators.find((o) => o.value === operator);
            return operatorOption && operatorOption.requiresValueB;
        }

        return false;
    },
    /**
     * Return true is the rule does not have an enterable value according to the selected operator.
     */
    hideValueInput: (rule: IRule): boolean => {
        const { attribute, operator } = rule;
        if (attribute && attribute.operators && operator) {
            const operatorOption = attribute.operators.find((o) => o.value === operator);
            return operatorOption && operatorOption.hideValueInput;
        }
        return false;
    },
    /**
     * Return true if rule is empty, false otherwise.
     */
    isEmpty: (rule: IRule): boolean => {
        const { attribute, operator, value, valueB } = rule;

        return (!attribute && !operator && !value && !valueB) ? true : false;
    },
    /**
     * Set the disableAddSelectedRules indicator on groups buttons based on the visible group buttons.
     * The add selected rules menu item should be enabled if there is at least one selected rule and
     * at least one of the selected rules is not already in the given group represented by the group
     * button.
     */
    prepDisableAddSelectedRules: (selectedRuleIds: string[], where: IRule[]): void => {
        const disableAll = !selectedRuleIds || !selectedRuleIds.length;
        let disableGroupId: string = null;

        if (!disableAll) {
            const selectedRules = selectedRuleIds ? where.filter((r) => selectedRuleIds.indexOf(r.$$ruleid) >= 0) : [];

            const distinctGroupIds = new Set<string>(); 
            selectedRules.forEach((r) => {
                distinctGroupIds.add(helper.getGroupId(r));
            });

            // if more than one group represented, then don't disable the add selected rules button on any.
            // but if only one is represented, disable the button on that one group
            if (distinctGroupIds.size === 1) {
                disableGroupId = Array.from(distinctGroupIds)[0];
            }
        }

        where.forEach((r) => {
            r.$$groupButtons.forEach((gb) => {
                if (!gb.showGroupButton) { // only care about visible buttons
                    return;
                }

                gb.disableAddSelectedRules = disableAll || (disableGroupId != null && disableGroupId === gb.groupId);
            });
        });
    },
    /**
     * Return the version to use for the query.
     */
    getVersion: (query: IQuery): number => {
        if (!query) {
            return version;
        }

        if (query.version != null) {
            return query.version;
        }

        const anyGroups = query.groups && query.groups.length > 0;
        const anyWhere = query.where && query.where.length > 0;

        if (anyGroups  || anyWhere) {
            return 0; // must be zero version if has groups or where but no version specified
        }

        return version;
    },
    /**
     * Change the condition of the group to the given condition.
     */
    changeGroupCondition: (groupId: string, condition: Condition, query: IQuery): IQuery => {
        if (!query || !query.groups) {
            return query;
        }

        const group = query.groups.find((g) => g.id === groupId);

        if (group) {
            group.condition = condition;
        }

        return query;
    }
};

export default helper;
